import { AuthorizedRoute } from '@/components/AuthorizedRoute/AuthorizedRoute'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { toaster } from '@/components/ui/toaster'
import { ApiariesProvider } from '@/contexts/useApiaries'
import {
  JSON_EDITOR_TAB_SIZE,
  getSystemConfigWithReadonlyFields,
} from '@/features/ClusterConfigs/components/ClusterRevisionForm/ClusterRevisionForm'
import {
  NewClusterForm,
  type NewClusterFormType,
  getNewClusterFormSchema,
} from '@/features/ClusterConfigs/components/NewClusterForm/NewClusterForm'
import { SubmitWithCommentPopover } from '@/features/ClusterConfigs/components/SubmitWithCommentPopover/SubmitWithCommentPopover'
import { clusterConfigTableRoute } from '@/features/ClusterConfigs/pages/ClusterConfigTable/ClusterConfigTable'
import { usePostCluster } from '@/generated/http-clients/v3ApiComponents'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
} from '@/hooks/useToast/toastUtils'
import { clustersRootRoute } from '@/router/rootRoutes'
import { useForm } from '@tanstack/react-form'
import { createRoute, useNavigate } from '@tanstack/react-router'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export const newClusterRoute = createRoute({
  getParentRoute: () => clustersRootRoute,
  path: 'new-hive',
  component: () => (
    <AuthorizedRoute>
      <ApiariesProvider>
        <NewClusterPage />
      </ApiariesProvider>
    </AuthorizedRoute>
  ),
})

const getFormSchema = (t: TFunction) =>
  getNewClusterFormSchema(t).extend({
    comment: z.string().min(1, 'Required field'),
  })

type FormValues = z.infer<ReturnType<typeof getFormSchema>>

const initialSystemConfig = { global: {} }

function NewClusterPage() {
  const { t } = useTranslation()
  const createCluster = usePostCluster({})
  const navigate = useNavigate()

  const breadCrumbs = [
    { name: t('sidebar.v3ClusterRevisions'), path: clusterConfigTableRoute.to },
    {
      name: t('translation:newClusterPage.title'),
      path: '',
    },
  ]

  const submitForm = async (values: FormValues) => {
    try {
      await createCluster.mutateAsync({
        pathParams: {
          orgId: values.organizationId,
        },
        body: {
          system: getSystemConfigWithReadonlyFields(values.system, {
            name: values.name,
            deployment_label: values.deploymentLabel,
          }),
          customer: values.customer,
          override: values.override,
          template_id: values.templateId,
          product_id: values.productId,
          changelog: values.comment,
        },
      })
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('success.createClusterConfig'),
      })
      return navigate({
        to: clustersRootRoute.to,
        search: { page: 1 } as { page: number },
      })
    } catch (error) {
      console.error('Failed to create a new cluster', error)
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('errors.submitConfig'),
        description: error instanceof Error ? error.message : '',
      })
    }
  }

  const form = useForm({
    defaultValues: {
      name: '',
      organizationId: '',
      deploymentLabel: '',
      templateId: '',
      productId: undefined as unknown as number,
      system: initialSystemConfig,
      customer: {},
      override: {},
      _system_raw_json: JSON.stringify(
        initialSystemConfig,
        null,
        JSON_EDITOR_TAB_SIZE,
      ),
      _override_raw_json: '{}',
      _customer_raw_json: '{}',
      comment: '',
    } as FormValues,
    validators: {
      onChange: getFormSchema(t),
    },
    onSubmit: async ({ value }) => {
      await submitForm(value)
    },
  })

  return (
    <PageLayout
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      title={t('pageTitle.createClusterConfig')}
      action={
        <form.Field name="comment">
          {(field) => {
            return (
              <SubmitWithCommentPopover
                onSubmit={form.handleSubmit}
                comment={field.state.value}
                commentError={
                  field.state.meta.isTouched
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
                isReadyToSubmit={form.state.canSubmit}
                onCommentChange={field.handleChange}
                translations={{
                  buttonText: t('translation:newClusterPage.submitButton'),
                  popoverTitle: t('translation:newClusterPage.popover.title'),
                  descriptionLabel: t(
                    'translation:newClusterPage.popover.descriptionLabel',
                  ),
                  commentPlaceholder: t(
                    'translation:newClusterPage.popover.descriptionHelperText',
                  ),
                  submitButtonText: t('translation:actions.submit'),
                  invalidConfigTooltip: t(
                    'features:clusterConfigDraft.validation.invalidConfig',
                  ),
                }}
              />
            )
          }}
        </form.Field>
      }
    >
      {/*  Type casting because TanstackForm has terrible TS public types  */}
      <NewClusterForm form={form as unknown as NewClusterFormType} />
    </PageLayout>
  )
}
